import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { PartnerProfile, UserRole } from "../models/account";
import { supabase } from "./supabase";
import { translateError } from "./errors";
import { sendPartnerSignupNotification } from "./telegram";
interface AuthResult {
  error: string | null;
  needsEmailConfirmation?: boolean;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: PartnerProfile | null;
  setProfile: (p: PartnerProfile | null) => void;
  role?: UserRole;
  loading: boolean;
  signUp: (
    name: string,
    email: string,
    password: string,
    extra?: { company?: string; phone?: string }
  ) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const fetchProfile = async (user: User): Promise<PartnerProfile | null> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .maybeSingle<PartnerProfile>();

  if (error) {
    console.error("Failed to load profile:", error.message);
    return null;
  }

  const metaName = (user.user_metadata?.name as string) ?? null;
  const metaCompany = (user.user_metadata?.company as string) ?? null;
  const metaPhone = (user.user_metadata?.phone as string) ?? null;

  if (!data) {
    const now = new Date().toISOString();
    const fallback: PartnerProfile = {
      id: user.id,
      role: "partner",
      name: metaName,
      email: user.email ?? null,
      phone: metaPhone,
      company: metaCompany,
      city: null,
      avatar_url: null,
      created_at: now,
      updated_at: now,
    };
    const { error: upsertError } = await supabase.from("profiles").upsert({
      id: fallback.id,
      name: fallback.name,
      email: fallback.email,
      company: fallback.company,
      phone: fallback.phone,
    });
    if (upsertError) {
      console.error("Failed to create profile:", upsertError.message);
      return null;
    }
    return fallback;
  }

  const missing: Record<string, string> = {};
  if (!data.company && metaCompany) missing.company = metaCompany;
  if (!data.phone && metaPhone) missing.phone = metaPhone;
  if (!data.name && metaName) missing.name = metaName;
  if (Object.keys(missing).length > 0) {
    const { error: syncError } = await supabase
      .from("profiles")
      .update(missing)
      .eq("id", user.id);
    if (syncError) {
      console.error("Failed to sync profile:", syncError.message);
    } else {
      Object.assign(data, missing);
    }
  }

  return data;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
      }
    );

    return () => subscription.subscription.unsubscribe();
  }, []);

  const user = session?.user ?? null;
  const role = profile?.role;

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }

    let cancelled = false;
    fetchProfile(user).then((p) => {
      if (!cancelled) setProfile(p);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const signUp = useCallback(
    async (
      name: string,
      email: string,
      password: string,
      extra?: { company?: string; phone?: string }
    ): Promise<AuthResult> => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, company: extra?.company, phone: extra?.phone },
        },
      });
      if (error) return { error: translateError(error) };
      if (data.user && data.session) {
        const { error: profileError } = await supabase.from("profiles").upsert({
          id: data.user.id,
          name,
          email,
          company: extra?.company ?? null,
          phone: extra?.phone ?? null,
        });
        if (profileError) {
          console.error("Failed to save profile:", profileError.message);
          return { error: translateError(profileError) };
        }
      }
      if (data.user) {
        sendPartnerSignupNotification({
          partner: {
            id: data.user.id,
            name,
            email,
            company: extra?.company ?? null,
            phone: extra?.phone ?? null,
          },
        }).catch((e) => console.error("Failed to send signup notification:", e));
      }
      return {
        error: null,
        needsEmailConfirmation: !!data.user && !data.session,
      };
    },
    []
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error ? translateError(error) : null };
    },
    []
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        setProfile,
        role,
        loading,
        signUp,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
